<template>
  <form
    class="form"
    method="POST"
    action="javascript:;"
    @submit.prevent="submit"
  >
    <FormError v-if="formError.length > 0" :errorMessages="formError" />

    <FormLoading v-if="!formReady" />

    <CardBase v-else>
      <template #header>{{ $route.meta.title }}</template>
      <template #content>
        <FormRow class="mb-4" :required="true">
          <template #label>Date / Time</template>
          <template #field>
            <date-picker
              v-model="formData.date"
              type="datetime"
              :editable="false"
              :show-second="false"
              :show-time-header="true"
              input-class="form__input"
              value-type="YYYY-MM-DD HH:mm:ss"
              format="DD MMMM YYYY - HH:mm"
              time-title-format="DD MMM YYYY"
              placeholder="Date / Time"
              :disabled-date="notAfterToday"
              :disabled-time="notAfterNow"
            ></date-picker>

            <div
              class="form-error"
              v-if="$v.formData.date.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.date.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true">
          <template #label>Category</template>
          <template #field v-if="!formCreate && formData.category.id == 4">
            <!-- Unable to change category -->
            <input
              type="text"
              class="form__input bg-gray-400 bg-opacity-25"
              disabled
              readonly
              :value="formData.category.name"
            />
          </template>
          <template #field v-else>
            <multiselect
              :allow-empty="false"
              deselectLabel=""
              :options="listBankTransactionCategory"
              :loading="listBankTransactionCategoryLoading"
              :class="$v.formData.category.$error ? 'multiselect--error' : ''"
              track-by="id"
              label="name"
              tag-placeholder="Press enter to choose"
              placeholder="Choose category"
              v-model="formData.category"
            >
              <template #noResult>No results found.</template>
            </multiselect>
            <div
              class="form-error"
              v-if="$v.formData.category.$error && formSubmitted"
            >
              <span
                class="form-error__text"
                v-if="!$v.formData.category.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true" v-if="isTransfer">
          <template #label>
            <template v-if="formData.transfer_from">Transfer From</template>
            <template v-else>Transfer To</template>
          </template>
          <template #field v-if="formCreate">
            <multiselect
              :options="listBankForTransfer"
              :loading="listBankForTransferLoading"
              :class="$v.formData.transferTo.$error ? 'multiselect--error' : ''"
              track-by="id"
              label="name"
              :placeholder="'Search bank for transfer'"
              v-model="formData.transferTo"
              :allow-empty="false"
              :deselectLabel="''"
              :internal-search="false"
              @search-change="getListBankForTransfer"
              @input="setTransferTo"
            >
              <template #noOptions>Search bank for transfer</template>
              <template #noResult>No results found.</template>
            </multiselect>
            <div
              class="form-error"
              v-if="$v.formData.transferTo.$error && formSubmitted"
            >
              <span
                class="form-error__text"
                v-if="!$v.formData.transferTo.required"
                >This field is required.</span
              >
            </div>
          </template>
          <template #field v-else>
            <input
              type="text"
              class="form__input bg-gray-400 bg-opacity-25"
              placeholder=""
              disabled
              readonly
              v-model="transferBank"
              v-currency="{
                currency: null,
                distractionFree: false,
                locale: 'en',
              }"
            />
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true">
          <template #label>Amount</template>
          <template #field>
            <input
              ref="amount"
              :class="{
                'form__input--error':
                  $v.formData.amount.$error && formSubmitted,
              }"
              type="text"
              class="form__input"
              placeholder="Amount"
              v-model="formData.amount"
              v-currency="{
                currency: null,
                locale: 'en',
                distractionFree: false,
                allowNegative: !isTransfer,
              }"
              @blur="checkType"
            />
            <button
              type="button"
              class="button button--primary mt-2"
              v-clipboard:copy="parseCurrency(formData.amount)"
            >
              Copy
              {{ parseCurrency(formData.amount) }}
            </button>
            <div
              class="form-error"
              v-if="$v.formData.amount.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.amount.required"
                >This field is required.</span
              >
            </div>
          </template>
          <template #help v-if="!isTransfer && formData.category"
            >Negative numbers are allowed</template
          >
        </FormRow>

        <FormRow class="mb-4" :required="true" v-if="!isTransfer">
          <template #label>Type</template>
          <template #field>
            <div class="flex">
              <button
                class="
                  border-green-500
                  button
                  border border-solid
                  hover:bg-green-500
                  hover:text-white
                  transition-all
                  duration-200
                  mr-2
                  text-left
                "
                :class="
                  transactionType == '1'
                    ? 'bg-green-500 text-white'
                    : 'text-green-500'
                "
                @click="setType(1)"
                type="button"
              >
                <span class="text-xs"
                  ><font-awesome-icon :icon="['fas', 'plus-circle']" />
                  Income</span
                >
              </button>
              <button
                class="
                  border-red-500
                  button
                  border border-solid
                  hover:bg-red-500
                  hover:text-white
                  transition-all
                  duration-200
                  text-left
                "
                :class="
                  transactionType == '2'
                    ? 'bg-red-500 text-white'
                    : 'text-red-500'
                "
                @click="setType(2)"
                type="button"
              >
                <span class="text-xs"
                  ><font-awesome-icon :icon="['fas', 'minus-circle']" />
                  Expense</span
                >
              </button>
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4">
          <template #label>Note</template>
          <template #field>
            <textarea
              type="text"
              class="form__input"
              placeholder="Note"
              v-model="formData.note"
              rows="5"
            ></textarea>
          </template>
        </FormRow>

        <FormDataLog :data="formData" v-if="!formCreate" />
      </template>

      <template #footer>
        <FormRow class="mb-0">
          <template #field>
            <BaseButton type="submit">Submit</BaseButton>
          </template>
          <template #link>
            <div class="ml-4">
              or
              <router-link
                :to="{
                  name: 'BankTransactionList',
                  params: {
                    bankId: $route.params.transferTo
                      ? $route.params.transferTo
                      : $route.params.bankId,
                  },
                }"
                class="link ml-2"
                >Back to List Transaction</router-link
              >
            </div>
          </template>
        </FormRow>
      </template>
    </CardBase>

    <CardBase v-if="!formCreate && getRole == 1 && formReady" class="mt-10">
      <template #header>Changelog</template>
      <template #content>
        <table class="w-full table-fixed">
          <thead v-if="!listLogsLoading">
            <tr>
              <th class="w-2/12">Date / Time</th>
              <th class="w-2/12">User</th>
              <th class="w-2/12">Action</th>
              <th class="text-left">Changes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="table__loader" colspan="4" v-if="listLogsLoading">
                Please wait
                <font-awesome-icon
                  :icon="['fas', 'spinner']"
                  spin
                  class="ml-2"
                />
              </td>
            </tr>
            <tr v-if="listLogs.length <= 0 && !listLogsLoading">
              <td class="text-center pt-6" colspan="4">No data found.</td>
            </tr>
            <tr v-for="row in listLogs" :key="row.id">
              <td>{{ row.created_at }}</td>
              <td>{{ row.created_by.name }}</td>
              <td>{{ row.action }}</td>
              <td>
                <pre
                  v-if="row.changes"
                  class="text-left"
                  v-html="printJson(row.changes)"
                ></pre>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </CardBase>
  </form>
</template>

<script>
import {
  CurrencyDirective,
  parse,
  setValue,
  getValue,
} from "vue-currency-input";
import { required, requiredIf } from "vuelidate/lib/validators";
import { submitFormMixin } from "@/mixins/submitFormMixin";
import { datePickerMixin } from "@/mixins/datePickerMixin";

import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";

const prettyPrintJson = require("pretty-print-json");

export default {
  directives: {
    currency: CurrencyDirective,
  },
  mixins: [submitFormMixin, datePickerMixin],
  components: {
    Multiselect,
    DatePicker,
  },
  props: {
    bankId: {
      type: Number,
      default: 0,
    },
  },
  data: () => {
    return {
      transactionType: 1,
      listBankTransactionCategory: [],
      listBankTransactionCategoryLoading: true,

      listBankForTransfer: [],
      listBankForTransferLoading: false,

      formData: {
        transferTo: null,
        transfer_to: {},
        date: null,
        amount: 0,
      },
      isTransfer: false,

      listLogs: [],
      listLogsLoading: true,

      transferTo: false,
      transferBank: null,
      disableCategory: true,
    };
  },
  validations: {
    formData: {
      category: {
        required,
      },
      amount: {
        required,
      },
      date: {
        required,
      },
      transferTo: {
        required: requiredIf(function () {
          return this.isTransfer && this.formCreate;
        }),
      },
    },
  },
  computed: {
    submitUrl() {
      return this.formCreate
        ? "/bank-account/" + this.$route.params.bankId + "/transaction"
        : "/bank-account/" +
            this.$route.params.bankId +
            "/transaction/" +
            this.$route.params.id;
    },
    fetchEndPoint() {
      return this.$route.meta.endpoint(this.$route);
    },
  },
  created() {
    this.$emit("getBankData");
    this.$_debounceTimer = null;

    if (this.formCreate) {
      const transferCategory = this.$route.query.is_transfer
        ? this.$route.query.is_transfer
        : 0;
      this.getBankTransactionCategory(transferCategory);
      this.formData.date = this.dateformat(new Date());
    }
  },
  methods: {
    checkType() {
      const amount = getValue(this.$refs.amount);

      if (amount < 0) {
        this.transactionType = 2;
      } else {
        this.transactionType = 1;
      }
    },
    setType(val) {
      this.transactionType = val;

      if (val == 2) {
        setValue(this.$refs.amount, getValue(this.$refs.amount) * -1);
      } else {
        setValue(this.$refs.amount, Math.abs(getValue(this.$refs.amount)));
      }
    },
    parseCurrency(amount) {
      return Math.abs(parse(amount));
    },
    setTransferTo(selectedOption) {
      this.formData.transfer_to.bank_account = selectedOption;
    },
    getBankTransferFrom(bankId) {
      this.$http
        .get(process.env.VUE_APP_API_URL + "/bank-account/" + bankId)
        .then((res) => {
          if (res.status == 200) {
            const { data } = res.data;
            this.transferBank = `[${data.bank.name}] - ${data.holder} (${data.number})`;
          }
        })
        .catch((err) => {
          if (err.response.status == 404) {
            this.$router.push({ name: this.$route.meta.baseRoute });
          } else if (err.response.status == 500) {
            // console.log("er");
          }
        })
        .finally(() => {});
    },
    printJson(data) {
      return data ? prettyPrintJson.toHtml(JSON.parse(data)) : "-";
    },
    getListLogs() {
      this.$http
        .get(process.env.VUE_APP_API_URL + this.$route.path + "/log")
        .then((res) => {
          if (res.status == 200) {
            this.listLogsLoading = false;

            const response = res.data;

            if (response) {
              this.listLogs = response;
            }
          }
        })
        .finally(() => {});
    },
    debounce(method, timer) {
      if (this.$_debounceTimer !== null) {
        clearTimeout(this.$_debounceTimer);
      }
      this.$_debounceTimer = setTimeout(() => {
        method();
      }, timer);
    },
    getListBankForTransfer(query) {
      if (query.length >= 3) {
        (this.listBankForTransfer = []),
          this.debounce(() => {
            this.listBankForTransferLoading = true;

            this.$http
              .get(
                process.env.VUE_APP_API_URL +
                  "/bank-account?exclude_id=" +
                  this.bankId +
                  "&search=" +
                  query
              )
              .then((res) => {
                if (res.status == 200) {
                  const response = res.data;

                  if (response) {
                    this.listBankForTransfer = response.data;
                  }
                }
              })
              .finally(() => {
                this.listBankForTransferLoading = false;
              });
          }, 250);
      }
    },
    fetchData(id) {
      this.formReady = false;

      const itemId = id ? id : this.formData.id;

      this.$http
        .get(
          process.env.VUE_APP_API_URL + "/" + this.fetchEndPoint + "/" + itemId
        )
        .then((res) => {
          if (res.status == 200) {
            const { data } = res.data;
            this.formData = { ...this.formData, ...data };
            this.formReady = true;

            if (data.transfer_to) {
              this.isTransfer = true;
              this.transferBank = data.transfer_to.bank_account.name;
            } else if (data.transfer_from) {
              this.isTransfer = true;
              this.transferBank = data.transfer_from.bank_account.name;
            }

            this.getBankTransactionCategory(data.category.is_transfer);
          }
        })
        .catch((err) => {
          if (err.response.status == 404) {
            this.$router.push({ name: this.$route.meta.baseRoute });
          } else if (err.response.status == 500) {
            this.formError = [];
            this.formError[0] = err.response.data.message;
          }
        })
        .finally(() => {});
    },
    dateformat(date) {
      return this.moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
    getBankTransactionCategory(transferOnly = "") {
      let transferParam = "";

      if (String(transferOnly)) {
        transferParam = "?is_transfer=" + transferOnly;
        if (transferOnly == "1") {
          this.isTransfer = true;
        }
      }

      this.$http
        .get(
          process.env.VUE_APP_API_URL +
            "/bank-transaction-category" +
            transferParam
        )
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listBankTransactionCategory = response.data;

              if (!this.formCreate) {
                let allowCategory = [];

                response.data.forEach((item) => {
                  allowCategory.push(item.id);
                });

                if (allowCategory.includes(this.formData.category.id)) {
                  this.disableCategory = false;
                }
              }
            }
          }
        })
        .finally(() => {
          this.listBankTransactionCategoryLoading = false;
        });
    },
  },
};
</script>
